/**
 * HelpHtml: renders help or AI answer HTML safely.
 *
 * Sanitised with its own DOMPurify instance (so its hooks never touch other DOMPurify users) using
 * the same allow-list as the server: p, br, h3, h4, ul, ol, li, strong, em, b, i, code, pre,
 * blockquote, a[href,title,target,rel] and span. No scripts, styles or event handlers.
 *
 * Links: console links (/app/..., /admin/...) navigate in the app without a reload; other links open
 * in a new tab.
 */

import { useMemo, type CSSProperties, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import classes from './help.module.css';
import { isInternalHref } from './helpUtils';

const ALLOWED_TAGS = ['p', 'br', 'h3', 'h4', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'code', 'pre', 'blockquote', 'a', 'span'];
const ALLOWED_ATTR = ['href', 'title', 'target', 'rel'];

type Purifier = ReturnType<typeof DOMPurify>;
let purifier: Purifier | null = null;

function getPurifier(): Purifier | null {
  if (typeof window === 'undefined') return null;
  if (purifier) return purifier;
  const instance = DOMPurify(window);
  instance.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName !== 'A') return;
    const el = node as Element;
    const href = el.getAttribute('href');
    if (isInternalHref(href)) {
      el.removeAttribute('target');
      el.removeAttribute('rel');
    } else if (href) {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }
  });
  purifier = instance;
  return instance;
}

/** Sanitises help HTML with the help allow-list. */
export function sanitizeHelpHtml(html: string | null | undefined): string {
  if (!html) return '';
  const p = getPurifier();
  if (!p) return '';
  return p.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOW_DATA_ATTR: false }) as string;
}

/** Plain text of help HTML (for previews and limits). */
export function helpHtmlToText(html: string | null | undefined): string {
  const clean = sanitizeHelpHtml(html);
  if (!clean || typeof document === 'undefined') return '';
  const div = document.createElement('div');
  div.innerHTML = clean;
  return (div.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export interface HelpHtmlProps {
  html?: string | null;
  /** Called after an in-app link inside the HTML navigated (e.g. to keep a drawer open or close a popover). */
  onNavigate?: (href: string) => void;
  className?: string;
  style?: CSSProperties;
  id?: string;
}

export function HelpHtml({ html, onNavigate, className, style, id }: HelpHtmlProps) {
  const navigate = useNavigate();
  const clean = useMemo(() => sanitizeHelpHtml(html), [html]);
  if (!clean) return null;

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!isInternalHref(href)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(href);
    onNavigate?.(href);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      id={id}
      className={[classes.html, className].filter(Boolean).join(' ')}
      style={style}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
